import type { GenericError, RequestStatus } from '@skynet/data-ws-core';

/**
 * MM WS commands. `@skynet/authz-balance-alert-rules` repeats the names as Cerbos kinds, and
 * `apps/dashboard/src/store/package-cerbos-kinds.test.ts` keeps them equal.
 */
export enum BalanceAlertRulesCommands {
  BalanceAlertRules = 'BalanceAlertRules',
  AddBalanceAlertRule = 'AddBalanceAlertRule',
  UpdateBalanceAlertRule = 'UpdateBalanceAlertRule',
  DeleteBalanceAlertRule = 'DeleteBalanceAlertRule',
}

/** When a rule fires: the compared value fell below the limit, or rose above it. */
export const BalanceAlertDirection = {
  Below: 0,
  Above: 1,
} as const;
export type BalanceAlertDirection =
  (typeof BalanceAlertDirection)[keyof typeof BalanceAlertDirection];

/** The result of the server's last (once-a-minute) check of a rule. */
export const BalanceAlertRuleStatus = {
  Normal: 0,
  Triggered: 1,
} as const;
export type BalanceAlertRuleStatus =
  (typeof BalanceAlertRuleStatus)[keyof typeof BalanceAlertRuleStatus];

/**
 * A balance alert rule as pushed by the server. A rule belongs to a project and covers both
 * loans and retainers.
 */
export interface BalanceAlertRule {
  id: number;
  creationDate: string;
  projectID: number;
  enabled: boolean;
  sendToOwners: boolean;
  /**
   * Auth user ids messaged in their Telegram private chat.
   * Contract agreed with the backend (SKY-5057), replacing `additionalDestinations`.
   */
  usersToNotify: number[];
  /**
   * Telegram group chat ids (e.g. `-1001234567890`); the alerts bot must be a member. A `long`
   * on the server (SKY-5064); Telegram keeps chat ids within 52 bits, so they are exact here.
   */
  telegramGroupsToNotify: number[];
  /** Only accounts that are enabled. */
  enabledAccountFilter: boolean;
  /** Substrings of an account name; empty means no filter. */
  keyWordsAccountFilter: string[];
  /** Exact `AccountID`s; empty means no filter. */
  idAccountFilter: number[];
  token: string;
  tokenLimit: number;
  direction: BalanceAlertDirection;
  /** Compare the value in USD instead of in token units. */
  compareInUSD: boolean;
  /** Compare the available balance only, without orders. */
  availableOnly: boolean;
  /** List every asset of the account in the alert, not just `token`. */
  showAllAssets: boolean;
  /**
   * The value of the account closest to (or furthest past) the limit; `null` until the rule
   * has been checked, or when no account matched.
   */
  currentValue: number | null;
  status: BalanceAlertRuleStatus;
}

type BalanceAlertRuleListKey =
  | 'usersToNotify'
  | 'telegramGroupsToNotify'
  | 'keyWordsAccountFilter'
  | 'idAccountFilter';

/** A rule as it comes over the wire: the server encodes an empty list as `null`. */
export type JsonBalanceAlertRule = Omit<BalanceAlertRule, BalanceAlertRuleListKey> & {
  [Key in BalanceAlertRuleListKey]: BalanceAlertRule[Key] | null;
};

export interface JsonBalanceAlertRulesSuccess {
  command: BalanceAlertRulesCommands.BalanceAlertRules;
  projectID: number;
  rules: JsonBalanceAlertRule[] | null;
}

/**
 * An enable switch flipped by the user but not yet seen in a push. The server publishes the
 * rules every 5 seconds, so the ack comes before the rule changes.
 */
export interface PendingEnabled {
  enabled: boolean;
  /**
   * Pushes since the ack; `null` before it. A push serialised just before the server applied
   * the change can still carry the old value, so a contradicting push only wins from the second.
   */
  pushesAfterAck: number | null;
}

export interface BalanceAlertRulesState {
  /** The project the stored `rules` belong to; `undefined` before the first push. */
  projectID: number | undefined;
  rules: BalanceAlertRule[];
  status: RequestStatus;
  /** The edit modal's update. */
  updateStatus: RequestStatus;
  /**
   * The enable switch's update. Both go out as `UpdateBalanceAlertRule` and the ack names only
   * the command, so one of them is in flight at a time and the ack goes to whichever it is.
   */
  toggleStatus: RequestStatus;
  deleteStatus: RequestStatus;
  /** Kept per operation so one command's failure can't surface under another's selector. */
  error: GenericError | null;
  updateError: GenericError | null;
  toggleError: GenericError | null;
  deleteError: GenericError | null;
  /** The rule the in-flight switch / delete is for; the ack does not name it. */
  togglingRuleId: number | null;
  deletingRuleId: number | null;
  /** Switch flips shown ahead of the push, by rule id. */
  pendingEnabled: Record<number, PendingEnabled>;
  /**
   * Rules acked as deleted that a push still carries, by rule id → pushes since the ack;
   * hidden until a push drops them (or keeps carrying them, see `PUSHES_TO_TRUST`).
   */
  deletedRules: Record<number, number>;
}
